import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { Clock, RefreshCw, AlertCircle, CheckCircle, Users } from 'lucide-react';

const APPS_URL = '/.netlify/functions/appsscript';

// Area scope per login (matches the portal's team-totals rules).
const SCOPE = {
  BOUVENGJ:   'ALL',
  BOUVENGP2:  'ALL',
  SOTOCRUZL:  'ALL',        // City TL sees all teams
  SOTOASTOL2: ['BWAY'],
  DONGB3:     ['DARL'],
};
const SCOPE_LABEL = { ALL: 'All contracts' };

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function AfternoonCheckPage() {
  const { currentUser } = useApp();
  const login = (currentUser?.login || '').toUpperCase();
  const areas = SCOPE[login] || 'ALL';
  const scopeLabel = Array.isArray(areas)
    ? areas.map(a => ({ BWAY: 'Broadway', DARL: 'Darlinghurst', CITY: 'City' }[a] || a)).join(', ')
    : (SCOPE_LABEL[areas] || areas);

  const [date, setDate]       = useState(todayISO());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [lastChecked, setLastChecked] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch(APPS_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getAfternoonCheck', date, areas }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Could not load check');
      setData(d);
      setLastChecked(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }));
    } catch (e) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [date, areas]);

  useEffect(() => { load(); }, [load]);

  const real  = (data?.unfinished || []).filter(u => !u.isStaff);
  const staff = (data?.unfinished || []).filter(u => u.isStaff);

  const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 20, marginBottom: 16 };
  const pill = (bg, bd) => ({ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: bg, border: `1px solid ${bd}`, borderRadius: 8, marginBottom: 8 });

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Clock size={22} /> Afternoon Check
          </div>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 3 }}>
            Who's still on shift and who hasn't logged depot bags — {scopeLabel}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="date" value={date} onChange={e => setDate(e.target.value)}
            style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 7, padding: '7px 10px', color: 'var(--hp-text)', fontSize: '0.82rem' }}
          />
          <button onClick={load} disabled={loading} style={{
            background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.35)', color: 'var(--hp-gold)',
            borderRadius: 7, padding: '7px 14px', fontWeight: 600, fontSize: '0.8rem', cursor: loading ? 'wait' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <RefreshCw size={14} style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined} /> Refresh
          </button>
        </div>
      </div>

      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--hp-dim)' }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          Checking…
        </div>
      )}
      {err && (
        <div style={{ ...card, color: 'var(--hp-red)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <AlertCircle size={16} /> {err}
        </div>
      )}

      {data && (
        <>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))', gap: 12, marginBottom: 16 }}>
            <div style={{ ...card, marginBottom: 0, textAlign: 'center' }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Finished</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'var(--hp-green)' }}>{data.finishedCount}</div>
            </div>
            <div style={{ ...card, marginBottom: 0, textAlign: 'center' }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Still on shift</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: real.length ? 'var(--hp-amber)' : 'var(--hp-green)' }}>{real.length}</div>
            </div>
            <div style={{ ...card, marginBottom: 0, textAlign: 'center' }}>
              <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Depot not logged</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: data.depotMissing.length ? '#60a5fa' : 'var(--hp-green)' }}>{data.depotMissing.length}</div>
            </div>
          </div>

          {/* Still on shift */}
          <div style={card}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Started but not finished ({real.length})
            </div>
            {real.length === 0 ? (
              <div style={{ fontSize: '0.85rem', color: 'var(--hp-green)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircle size={15} /> All drivers have finished their shift.
              </div>
            ) : real.map(u => (
              <div key={u.login} style={pill('rgba(245,158,11,0.08)', 'rgba(245,158,11,0.25)')}>
                <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>
                  <strong>{u.login}</strong>{u.name && u.name !== u.login ? ` · ${u.name}` : ''}
                </span>
                <span style={{ fontSize: '0.74rem', color: 'var(--hp-amber)', fontWeight: 700 }}>
                  {u.area || '—'}{u.state === 'on_break' ? ' · on break' : ''}
                </span>
              </div>
            ))}
            {staff.length > 0 && (
              <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Users size={12} /> Also logged in (staff/admin): {staff.map(u => u.login).join(', ')}
              </div>
            )}
          </div>

          {/* Finished — with finish times so the day reconciles at a glance */}
          <div style={card}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Finished ({(data.finished || []).length})
            </div>
            {(data.finished || []).length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No shifts submitted yet today.</div>
            ) : (data.finished || []).map(f => (
              <div key={f.login} style={pill('rgba(52,211,153,0.07)', 'rgba(52,211,153,0.25)')}>
                <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>
                  <strong>{f.login}</strong>{f.name && f.name !== f.login ? ` · ${f.name}` : ''}
                  {f.isStaff ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}> (staff)</span> : ''}
                </span>
                <span style={{ fontSize: '0.8rem', color: 'var(--hp-green)', fontWeight: 700, fontFamily: 'var(--font-mono, monospace)' }}>
                  {f.finishTime}{f.area ? <span style={{ color: 'var(--hp-dim)', fontWeight: 600, marginLeft: 8 }}>{f.area}</span> : ''}
                </span>
              </div>
            ))}
          </div>

          {/* Depot not logged */}
          <div style={card}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
              Depot bags not logged ({data.depotMissing.length})
            </div>
            {data.depotMissing.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>
                {data.depotLoggedCount > 0
                  ? '✓ All active depot drivers have logged their count.'
                  : 'No depot roster configured — set active depot drivers in DoE & Depots → Manage depot drivers.'}
              </div>
            ) : data.depotMissing.map(m => (
              <div key={m.login} style={pill('rgba(96,165,250,0.08)', 'rgba(96,165,250,0.25)')}>
                <span style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}><strong>{m.login}</strong></span>
                <span style={{ fontSize: '0.74rem', color: '#60a5fa', fontWeight: 700 }}>{m.area || m.route || '—'}</span>
              </div>
            ))}
          </div>

          {lastChecked && (
            <div style={{ textAlign: 'center', fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
              Last checked {lastChecked} · {data.depotLoggedCount} depot entr{data.depotLoggedCount === 1 ? 'y' : 'ies'} logged today
            </div>
          )}
        </>
      )}
    </div>
  );
}
